import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState, memo } from "react";
import { Link } from "@tanstack/react-router";
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
  useVelocity,
  useMotionValueEvent,
} from "framer-motion";
import {
  CheckCircle,
  Menu,
  X,
  ArrowUp,
  ArrowRight,
  Zap,
  ShoppingBag,
  Gauge,
  Search,
  Sparkles,
  Plus,
  Minus,
  PhoneCall,
  LayoutTemplate,
  Rocket,
  Mail,
  User,
  MessageSquare,
  Clock,
  ShieldCheck,
  Instagram,
  Linkedin,
  Loader2,
  Check,
  TrendingUp,
  CreditCard,
  Calendar,
  FileText,
} from "lucide-react";
import { z } from "zod";
import { toast } from "sonner";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Tworzenie Stron Internetowych | Darmowe Demo w 72h" },
      {
        name: "description",
        content:
          "Skuteczne strony internetowe i sklepy e-commerce, które sprzedają. Tworzymy projekt pod Twój biznes. Sprawdź pakiety cenowe i odbierz bezpłatne demo!",
      },
      {
        name: "keywords",
        content:
          "tworzenie stron internetowych, strony www, sklepy internetowe, e-commerce, strony B2B, agencja interaktywna, Next.js, SEO, PageSpeed 100, darmowe demo, projektowanie stron Polska",
      },
      { property: "og:title", content: "Tworzenie Stron Internetowych | Darmowe Demo w 72h" },
      {
        property: "og:description",
        content:
          "Skuteczne strony internetowe i sklepy e-commerce, które sprzedają. Tworzymy projekt pod Twój biznes. Sprawdź pakiety cenowe i odbierz bezpłatne demo!",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://skutecznestrony.com/" },
      {
        property: "og:image",
        content:
          "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/c595c1e4-d5f7-4690-92f3-74791ec5950c",
      },
      { property: "og:image:alt", content: "SkuteczneStrony — nowoczesne strony internetowe i sklepy e-commerce" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Tworzenie Stron Internetowych | Darmowe Demo w 72h" },
      {
        name: "twitter:description",
        content:
          "Skuteczne strony internetowe i sklepy e-commerce, które sprzedają. Odbierz bezpłatne demo w 72h.",
      },
      {
        name: "twitter:image",
        content:
          "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/c595c1e4-d5f7-4690-92f3-74791ec5950c",
      },
    ],
    links: [
      { rel: "canonical", href: "https://skutecznestrony.com/" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          "@id": "https://skutecznestrony.com/#business",
          name: "SkuteczneStrony",
          url: "https://skutecznestrony.com/",
          image:
            "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/c595c1e4-d5f7-4690-92f3-74791ec5950c",
          description:
            "Studio projektowo-developerskie specjalizujące się w błyskawicznym tworzeniu nowoczesnych stron internetowych Next.js, wizytówek B2B oraz zaawansowanych systemów e-commerce z unikalną ofertą darmowego demo w 72 godziny.",
          priceRange: "$$",
          areaServed: { "@type": "Country", name: "Poland" },
          serviceType: [
            "Tworzenie stron internetowych",
            "Sklepy e-commerce",
            "Strony B2B i wizytówki",
            "Optymalizacja SEO i PageSpeed",
            "Integracje płatności i Booksy",
          ],
          inLanguage: "pl-PL",
          email: "kontakt@skutecznestrony.com",
          slogan: "Strony, które pracują dla Ciebie.",
          knowsAbout: [
            "Next.js",
            "React",
            "TanStack Start",
            "E-commerce",
            "SEO techniczne",
            "Core Web Vitals",
            "Integracje Booksy",
            "Google Business Profile",
          ],
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "Pakiety SkuteczneStrony",
            itemListElement: [
              {
                "@type": "Offer",
                position: 1,
                name: "Klasyczna Wizytówka START",
                description:
                  "Profesjonalna wizytówka B2B z formularzem kontaktowym, sekcją oferty i podstawową optymalizacją SEO. Realizacja od 72h.",
                price: "1899",
                priceCurrency: "PLN",
                availability: "https://schema.org/InStock",
                itemOffered: {
                  "@type": "Service",
                  name: "Klasyczna Wizytówka START",
                  serviceType: "Strona wizytówka B2B",
                },
              },
              {
                "@type": "Offer",
                position: 2,
                name: "Interaktywna Platforma GROWTH",
                description:
                  "Wielosekcyjna strona z animacjami, blogiem, integracjami i pełnym pakietem SEO. Idealna dla rozwijających się firm.",
                price: "2499",
                priceCurrency: "PLN",
                availability: "https://schema.org/InStock",
                itemOffered: {
                  "@type": "Service",
                  name: "Interaktywna Platforma GROWTH",
                  serviceType: "Interaktywna strona firmowa",
                },
              },
              {
                "@type": "Offer",
                position: 3,
                name: "Dedykowany E-commerce i Custom INFINITY",
                description:
                  "Zaawansowany sklep internetowy lub dedykowana aplikacja z integracjami płatności, faktur i CRM. Wycena indywidualna.",
                priceSpecification: {
                  "@type": "PriceSpecification",
                  priceCurrency: "PLN",
                  price: "0",
                  description: "Wycena indywidualna",
                },
                availability: "https://schema.org/InStock",
                itemOffered: {
                  "@type": "Service",
                  name: "Dedykowany E-commerce INFINITY",
                  serviceType: "Sklep e-commerce i aplikacje custom",
                },
              },
            ],
          },
          makesOffer: [
            { "@type": "Offer", name: "Darmowe demo w 72h", price: "0", priceCurrency: "PLN" },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Ile kosztuje stworzenie strony internetowej w SkuteczneStrony?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Pakiet START kosztuje 1899 PLN, GROWTH 2499 PLN, a dedykowane sklepy e-commerce i projekty INFINITY są wyceniane indywidualnie w zależności od zakresu.",
              },
            },
            {
              "@type": "Question",
              name: "W jakim czasie powstaje strona internetowa?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Darmowe demo Twojej nowej strony przygotowujemy w 72 godziny. Pełna realizacja zajmuje zwykle od 1 do 4 tygodni w zależności od pakietu.",
              },
            },
            {
              "@type": "Question",
              name: "Czy strony są zoptymalizowane pod SEO i PageSpeed?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Tak. Każda strona powstaje w Next.js z myślą o Core Web Vitals i wyniku PageSpeed 100/100. SEO techniczne i podstawowa optymalizacja słów kluczowych są wliczone w cenę.",
              },
            },
            {
              "@type": "Question",
              name: "Czy obsługujecie sklepy internetowe i integracje płatności?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Tak. Tworzymy sklepy e-commerce z integracją Stripe, Przelewy24, automatycznymi fakturami oraz Booksy do rezerwacji online.",
              },
            },
          ],
        }),
      },
    ],
  }),
  component: Index,
});

const EASE = [0.16, 1, 0.3, 1] as const;

const pseudoRandom = (index: number, salt = 0) => {
  const value = Math.sin(index * 12.9898 + salt * 78.233) * 43758.5453;
  return value - Math.floor(value);
};

const round3 = (value: number) => Math.round(value * 1000) / 1000;

/* -------------------- LOGO -------------------- */
const Logo = memo(function Logo({ size = 28 }: { size?: number }) {
  return (
    <span className="flex items-center gap-2.5" aria-label="SkuteczneStrony">
      <span
        className="relative inline-flex items-center justify-center"
        style={{ width: size, height: size }}
      >
        <svg
          viewBox="0 0 40 40"
          width={size}
          height={size}
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="lg" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#00F5A0" />
              <stop offset="100%" stopColor="#6366F1" />
            </linearGradient>
          </defs>
          <rect x="2" y="2" width="36" height="36" rx="10" fill="url(#lg)" />
          <path
            d="M13 25c2.2 2 5 3 7.5 3 3.5 0 6-1.7 6-4.3 0-2.4-2-3.5-5.6-4.4-4.6-1.1-7.4-2.5-7.4-6.3 0-3.5 3-5.7 7.4-5.7 2.6 0 5 .8 6.7 2"
            fill="none"
            stroke="#02050A"
            strokeWidth="3"
            strokeLinecap="round"
          />
        </svg>
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-[10px]"
          style={{ boxShadow: "0 0 12px rgba(0,245,160,0.45)" }}
        />
      </span>
      <span className="font-black tracking-tight text-white">
        Skuteczne<span className="text-[#00F5A0]">Strony</span>
      </span>
    </span>
  );
});

/* -------------------- MAGNETIC CURSOR -------------------- */
const Cursor = memo(function Cursor() {
  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const ringX = useSpring(x, { stiffness: 150, damping: 20, mass: 0.5 });
  const ringY = useSpring(y, { stiffness: 150, damping: 20, mass: 0.5 });
  const vx = useVelocity(x);
  const vy = useVelocity(y);
  const scaleX = useSpring(useTransform(vx, [-2000, 0, 2000], [1.4, 1, 1.4]), {
    stiffness: 200,
    damping: 20,
  });
  const scaleY = useSpring(useTransform(vy, [-2000, 0, 2000], [1.4, 1, 1.4]), {
    stiffness: 200,
    damping: 20,
  });
  const [hovering, setHovering] = useState(false);
  const [touch, setTouch] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia("(pointer: coarse)").matches) {
      setTouch(true);
      return;
    }
    let lastEv: PointerEvent | null = null;
    let raf = 0;
    let lastTarget: EventTarget | null = null;
    const flush = () => {
      raf = 0;
      if (!lastEv) return;
      x.set(lastEv.clientX);
      y.set(lastEv.clientY);
      if (lastEv.target !== lastTarget) {
        lastTarget = lastEv.target;
        const t = lastEv.target as HTMLElement | null;
        setHovering(!!t?.closest?.("button,a,[data-magnetic]"));
      }
    };
    const move = (e: PointerEvent) => {
      lastEv = e;
      if (!raf) raf = requestAnimationFrame(flush);
    };
    window.addEventListener("pointermove", move, { passive: true });
    document.documentElement.style.cursor = "none";
    return () => {
      window.removeEventListener("pointermove", move);
      if (raf) cancelAnimationFrame(raf);
      document.documentElement.style.cursor = "";
    };
  }, [x, y]);

  if (touch) return null;
  return (
    <>
      <motion.div
        className="pointer-events-none fixed left-0 top-0 z-[1000] rounded-full bg-white"
        style={{
          x,
          y,
          translateX: "-50%",
          translateY: "-50%",
          width: hovering ? 80 : 12,
          height: hovering ? 80 : 12,
          mixBlendMode: "difference",
          scaleX,
          scaleY,
          transition: "width 0.3s, height 0.3s",
        }}
      />
      <motion.div
        className="pointer-events-none fixed left-0 top-0 z-[999] h-8 w-8 rounded-full border border-white/30"
        style={{ x: ringX, y: ringY, translateX: "-50%", translateY: "-50%" }}
      />
    </>
  );
});

/* -------------------- NAV -------------------- */
const NAV_LINKS: { label: string; href: string; route?: false }[] = [
  { label: "Co robimy", href: "#O nas" },
  { label: "Proces", href: "#Proces" },
  { label: "Cennik", href: "#Cennik" },
  { label: "Dodatki", href: "#Dodatki" },
];

function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      {/* Desktop */}
      <motion.nav
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: EASE, delay: 0.3 }}
        className="fixed left-1/2 top-6 z-[900] hidden -translate-x-1/2 md:block"
      >
        <div
          className={`flex items-center gap-2 rounded-full px-3 py-2 shadow-2xl shadow-black/50 transition-all duration-500 ${
            scrolled
              ? "border border-white/10 bg-black/30 backdrop-blur-2xl"
              : "border border-transparent bg-transparent"
          }`}
        >
          <a href="#top" className="flex items-center px-4 py-2">
            <span
              className="bg-clip-text text-[14px] font-semibold tracking-tight text-transparent"
              style={{
                backgroundImage: "linear-gradient(110deg,#00F5A0 0%,#6366F1 100%)",
              }}
            >
              SkuteczneStrony
            </span>
          </a>
          <ul className="relative flex items-center gap-1">
            {NAV_LINKS.map((l) => (
              <li key={l.label} onMouseEnter={() => setActive(l.label)} onMouseLeave={() => setActive(null)}>
                <a
                  href={l.href}
                  className="relative block rounded-full px-4 py-2 text-sm font-medium text-slate-300 transition hover:text-white"
                >
                  {active === l.label && (
                    <motion.span
                      layoutId="navpill"
                      className="absolute inset-0 -z-10 rounded-full bg-white/10"
                      transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    />
                  )}
                  {l.label}
                </a>
              </li>
            ))}
            <li>
              <Link
                to="/marketing"
                className="relative block rounded-full px-4 py-2 text-sm font-medium text-slate-300 transition hover:text-white"
              >
                Marketing
              </Link>
            </li>
            <li>
              <Link
                to="/demo"
                className="relative block rounded-full px-4 py-2 text-sm font-medium text-slate-300 transition hover:text-white"
              >
                Demo
              </Link>
            </li>
          </ul>
          <a
            href="#cta"
            className="ml-2 rounded-full bg-gradient-to-r from-[#00F5A0] to-[#6366F1] px-5 py-2.5 text-sm font-black text-black shadow-lg shadow-[#00F5A0]/30 transition hover:brightness-110"
          >
            Porozmawiajmy
          </a>
        </div>
      </motion.nav>

      {/* Mobile — liquid glass pill */}
      <div className="fixed inset-x-0 top-3 z-[900] flex justify-center px-4 md:hidden">
        <motion.div
          layout
          transition={{ type: "spring", stiffness: 260, damping: 28 }}
          className="relative w-full max-w-md overflow-hidden rounded-[28px] border border-white/10 bg-white/[0.04] shadow-[0_10px_40px_-10px_rgba(0,0,0,0.7)] backdrop-blur-2xl backdrop-saturate-150"
        >
          {/* glass highlights */}
          <div className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/[0.06] via-transparent to-transparent" />
          <div className="pointer-events-none absolute -left-10 -top-16 h-40 w-40 rounded-full bg-[#00F5A0]/15 blur-3xl" />
          <div className="pointer-events-none absolute -right-10 -bottom-16 h-40 w-40 rounded-full bg-[#6366F1]/15 blur-3xl" />

          <motion.div layout className="relative flex h-14 items-center justify-end pr-2">
            <a
              href="#top"
              onClick={() => setOpen(false)}
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            >
              <span
                className="bg-clip-text text-[15px] font-semibold tracking-tight text-transparent"
                style={{
                  backgroundImage: "linear-gradient(110deg,#00F5A0 0%,#6366F1 100%)",
                }}
              >
                SkuteczneStrony
              </span>
            </a>
            <button
              onClick={() => setOpen((o) => !o)}
              className="relative flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/[0.06] text-white transition active:scale-95"
              aria-label="Menu"
              aria-expanded={open}
            >
              <motion.span
                animate={{ rotate: open ? 90 : 0, opacity: open ? 0 : 1 }}
                transition={{ duration: 0.2 }}
                className="absolute"
              >
                <Menu size={18} />
              </motion.span>
              <motion.span
                animate={{ rotate: open ? 0 : -90, opacity: open ? 1 : 0 }}
                transition={{ duration: 0.2 }}
                className="absolute"
              >
                <X size={18} />
              </motion.span>
            </button>
          </motion.div>

          <AnimatePresence initial={false}>
            {open && (
              <motion.div
                key="panel"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.35, ease: EASE }}
                className="relative overflow-hidden"
              >
                <div className="mx-4 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                <motion.ul
                  initial="hidden"
                  animate="show"
                  exit="hidden"
                  variants={{
                    show: { transition: { staggerChildren: 0.04, delayChildren: 0.05 } },
                    hidden: { transition: { staggerChildren: 0.02, staggerDirection: -1 } },
                  }}
                  className="flex flex-col gap-1 px-2 py-3"
                >
                  {NAV_LINKS.map((l) => (
                    <motion.li
                      key={l.label}
                      variants={{
                        hidden: { opacity: 0, x: -8 },
                        show: { opacity: 1, x: 0 },
                      }}
                    >
                      <a
                        href={l.href}
                        onClick={() => setOpen(false)}
                        className="flex items-center justify-between rounded-2xl px-4 py-3 text-base font-medium text-slate-200 transition hover:bg-white/[0.05] hover:text-white"
                      >
                        <span>{l.label}</span>
                        <ArrowRight size={14} className="text-slate-500" />
                      </a>
                    </motion.li>
                  ))}
                  <motion.li
                    variants={{
                      hidden: { opacity: 0, x: -8 },
                      show: { opacity: 1, x: 0 },
                    }}
                  >
                    <Link
                      to="/marketing"
                      onClick={() => setOpen(false)}
                      className="flex items-center justify-between rounded-2xl px-4 py-3 text-base font-medium text-slate-200 transition hover:bg-white/[0.05] hover:text-white"
                    >
                      <span>Marketing</span>
                      <ArrowRight size={14} className="text-slate-500" />
                    </Link>
                  </motion.li>
                  <motion.li
                    variants={{
                      hidden: { opacity: 0, x: -8 },
                      show: { opacity: 1, x: 0 },
                    }}
                  >
                    <Link
                      to="/demo"
                      onClick={() => setOpen(false)}
                      className="flex items-center justify-between rounded-2xl px-4 py-3 text-base font-medium text-slate-200 transition hover:bg-white/[0.05] hover:text-white"
                    >
                      <span>Demo</span>
                      <ArrowRight size={14} className="text-slate-500" />
                    </Link>
                  </motion.li>
                  <motion.li
                    variants={{
                      hidden: { opacity: 0, y: 8 },
                      show: { opacity: 1, y: 0 },
                    }}
                    className="mt-2 px-2 pb-1"
                  >
                    <a
                      href="#cta"
                      onClick={() => setOpen(false)}
                      className="flex items-center justify-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-semibold text-black transition active:scale-[0.98]"
                    >
                      Porozmawiajmy
                      <ArrowRight size={14} />
                    </a>
                  </motion.li>
                </motion.ul>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </>
  );
}

/* -------------------- HERO -------------------- */
function KineticHeadline({ text }: { text: string }) {
  const mouseX = useMotionValue(-9999);
  const mouseY = useMotionValue(-9999);
  const words = text.split(" ");
  let letterIdx = 0;
  return (
    <h1
      onPointerMove={(e) => {
        mouseX.set(e.clientX);
        mouseY.set(e.clientY);
      }}
      onPointerLeave={() => {
        mouseX.set(-9999);
        mouseY.set(-9999);
      }}
      className="relative max-w-6xl text-center text-[8vw] font-black leading-[0.92] tracking-tight text-white sm:text-[6vw] md:text-[4.6rem] lg:text-[5.6rem]"
      style={{ textShadow: "0 10px 40px rgba(255,255,255,0.1)", perspective: 800 }}
    >
      {words.map((word, wi) => (
        <span key={wi} className="inline-block whitespace-nowrap">
          {word.split("").map((ch) => {
            const idx = letterIdx++;
            const isLux = word === "działają.";
            return (
              <Letter
                key={idx}
                ch={ch}
                idx={idx}
                mouseX={mouseX}
                mouseY={mouseY}
                accent={isLux}
              />
            );
          })}
          {wi < words.length - 1 && <span className="inline-block">&nbsp;</span>}
        </span>
      ))}
    </h1>
  );
}

function Letter({
  ch,
  idx,
  mouseX,
  mouseY,
  accent,
}: {
  ch: string;
  idx: number;
  mouseX: ReturnType<typeof useMotionValue<number>>;
  mouseY: ReturnType<typeof useMotionValue<number>>;
  accent: boolean;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const x = useSpring(0, { stiffness: 150, damping: 15, mass: 0.5 });
  const y = useSpring(0, { stiffness: 150, damping: 15, mass: 0.5 });

  useMotionValueEvent(mouseX, "change", () => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const dx = cx - mouseX.get();
    const dy = cy - mouseY.get();
    const dist = Math.hypot(dx, dy);
    if (dist < 100) {
      const f = (100 - dist) / 4;
      x.set((dx / dist) * f);
      y.set((dy / dist) * f);
    } else {
      x.set(0);
      y.set(0);
    }
  });

  return (
    <motion.span
      ref={ref}
      initial={{ y: 100, rotateX: -90, opacity: 0 }}
      animate={{ y: 0, rotateX: 0, opacity: 1 }}
      transition={{ delay: 0.5 + idx * 0.03, duration: 0.9, ease: EASE }}
      style={{
        x,
        y,
        display: "inline-block",
        transformStyle: "preserve-3d",
        backgroundImage: accent
          ? "linear-gradient(120deg,#00F5A0,#6366F1,#00F5A0)"
          : undefined,
        backgroundSize: accent ? "200% 100%" : undefined,
        WebkitBackgroundClip: accent ? "text" : undefined,
        WebkitTextFillColor: accent ? "transparent" : undefined,
        animation: accent ? "gradPan 6s linear infinite" : undefined,
        willChange: "transform",
      }}
    >
      {ch}
    </motion.span>
  );
}

function Hero() {
  const { scrollY } = useScroll();
  const opacity = useTransform(scrollY, (v) =>
    Math.max(0, Math.min(1, 1 - v / 600)),
  );
  const parallaxY = useTransform(scrollY, (v) => v * 0.25);
  const orbX = useMotionValue(0);
  const orbY = useMotionValue(0);
  const sx = useSpring(orbX, { stiffness: 30, damping: 25, mass: 1.2 });
  const sy = useSpring(orbY, { stiffness: 30, damping: 25, mass: 1.2 });

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia("(pointer: coarse)").matches) return;
    let lastEv: PointerEvent | null = null;
    let raf = 0;
    const flush = () => {
      raf = 0;
      if (!lastEv) return;
      orbX.set((lastEv.clientX - window.innerWidth / 2) * 0.08);
      orbY.set((lastEv.clientY - window.innerHeight / 2) * 0.08);
    };
    const move = (e: PointerEvent) => {
      lastEv = e;
      if (!raf) raf = requestAnimationFrame(flush);
    };
    window.addEventListener("pointermove", move, { passive: true });
    return () => {
      window.removeEventListener("pointermove", move);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [orbX, orbY]);

  return (
    <section
      id="top"
      className="relative flex min-h-[100dvh] flex-col items-center justify-center overflow-hidden px-6"
    >
      {/* Ambient gradient mesh */}
      <motion.div
        style={{ opacity }}
        className="pointer-events-none absolute inset-0 z-0"
        aria-hidden
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
            maskImage:
              "radial-gradient(ellipse 70% 60% at 50% 45%, black 30%, transparent 75%)",
            WebkitMaskImage:
              "radial-gradient(ellipse 70% 60% at 50% 45%, black 30%, transparent 75%)",
          }}
        />
      </motion.div>

      <motion.div
        className="pointer-events-none absolute left-1/2 top-1/2 z-0 h-[70vw] w-[70vw] -translate-x-1/2 -translate-y-1/2 rounded-full transform-gpu"
        style={{
          x: sx,
          y: sy,
          background:
            "radial-gradient(circle at 30% 30%, rgba(0,245,160,0.22), transparent 60%), radial-gradient(circle at 70% 70%, rgba(99,102,241,0.18), transparent 60%)",
          filter: "blur(120px)",
          willChange: "transform",
        }}
        animate={{ scale: [1, 1.06, 1] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />

      <motion.div
        style={{ y: parallaxY, opacity }}
        className="relative z-20 flex flex-col items-center"
      >
        <h1
          className="max-w-5xl text-center text-[clamp(2.6rem,8vw,6.5rem)] font-semibold leading-[0.95] tracking-[-0.04em] text-white"
          style={{ fontFeatureSettings: '"ss01","cv11"' }}
        >
          Strony, które{" "}
          <span
            className="bg-clip-text text-transparent"
            style={{
              backgroundImage:
                "linear-gradient(110deg,#FFFFFF 0%,#00F5A0 45%,#6366F1 100%)",
            }}
          >
            pracują
          </span>{" "}
          dla&nbsp;Ciebie.
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.9, ease: EASE }}
          className="mt-7 max-w-xl text-center text-base leading-relaxed text-slate-400 sm:text-lg"
        >
          Projektujemy i budujemy nowoczesne strony internetowe. Szybkie, dopracowane, gotowe do działania.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.9, ease: EASE }}
          className="mt-10 flex flex-col items-center gap-3 sm:flex-row"
        >
          <motion.a
            href="#cta"
            data-magnetic
            whileTap={{ scale: 0.97 }}
            className="group relative inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-black transition-transform duration-300 hover:scale-[1.03] sm:text-base"
          >
            <span className="relative z-10">Rozpocznij projekt</span>
            <ArrowRight size={16} className="relative z-10 transition-transform duration-300 group-hover:translate-x-0.5" />
            <span className="absolute inset-0 -z-10 rounded-full bg-[#00F5A0] opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-70" />
          </motion.a>
          <motion.a
            href="#Cennik"
            data-magnetic
            whileTap={{ scale: 0.97 }}
            className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-7 py-3.5 text-sm font-medium text-white backdrop-blur-xl transition hover:bg-white/[0.07] sm:text-base"
          >
            Zobacz cennik
          </motion.a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        style={{ opacity }}
        className="absolute bottom-10 left-1/2 z-20 -translate-x-1/2"
      >
        <div className="relative h-14 w-px overflow-hidden bg-gradient-to-b from-transparent via-white/30 to-transparent">
          <motion.span
            className="absolute left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-[#00F5A0] shadow-[0_0_12px_#00F5A0]"
            animate={{ top: ["-10%", "110%"] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
      </motion.div>

      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-[#030508]" />
    </section>
  );
}

/* -------------------- STATEMENT -------------------- */
function Statement() {
  return (
    <section className="relative mx-auto max-w-6xl px-4 py-24 sm:px-6 md:py-36 lg:px-12 xl:px-16">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 1, ease: EASE }}
        className="relative overflow-hidden rounded-[32px] border border-white/[0.07] bg-[rgba(8,12,22,0.55)] px-6 py-16 backdrop-blur-3xl sm:px-12 sm:py-20 md:px-20 md:py-28"
      >
        {/* ambient glow */}
        <div
          className="pointer-events-none absolute -left-32 -top-32 h-80 w-80 rounded-full opacity-60"
          style={{ background: "radial-gradient(circle,#00F5A055,transparent 60%)", filter: "blur(40px)" }}
        />
        <div
          className="pointer-events-none absolute -bottom-32 -right-32 h-80 w-80 rounded-full opacity-50"
          style={{ background: "radial-gradient(circle,#6366F155,transparent 60%)", filter: "blur(40px)" }}
        />
        {/* top edge highlight */}
        <div
          className="pointer-events-none absolute inset-x-16 top-0 h-px opacity-60"
          style={{ background: "linear-gradient(90deg,transparent,#00F5A0AA,transparent)" }}
        />
        <span className="pointer-events-none absolute left-4 top-2 select-none text-[10rem] leading-none text-white/[0.04] sm:left-8 sm:text-[14rem] md:text-[18rem]">
          “
        </span>
        <h2 className="relative mx-auto max-w-4xl text-center text-[clamp(1.8rem,5vw,3.5rem)] font-semibold leading-[1.1] tracking-[-0.03em] text-white">
          Zwykła strona to koszt. Dobra strona to{" "}
          <span
            className="bg-clip-text text-transparent"
            style={{
              backgroundImage: "linear-gradient(110deg,#00F5A0 0%,#6366F1 100%)",
            }}
          >
            inwestycja
          </span>
          .
        </h2>
        <p className="relative mx-auto mt-6 max-w-xl text-center text-sm leading-relaxed text-slate-400 sm:text-base">
          Każda złotówka włożona w dobrze zaprojektowaną stronę wraca jako zaufanie, klienci i sprzedaż.
        </p>
      </motion.div>
    </section>
  );
}

/* -------------------- BENTO -------------------- */
function BentoCard({
  className = "",
  children,
  accent = "#00F5A0",
}: {
  className?: string;
  children: React.ReactNode;
  accent?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const glow = useTransform(
    [mx, my],
    ([x, y]: number[]) =>
      `radial-gradient(420px circle at ${(x as number) * 100}% ${(y as number) * 100}%, ${accent}26, transparent 60%)`,
  );

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.9, ease: EASE }}
      onPointerMove={(e) => {
        const r = ref.current?.getBoundingClientRect();
        if (!r) return;
        mx.set((e.clientX - r.left) / r.width);
        my.set((e.clientY - r.top) / r.height);
      }}
      onPointerLeave={() => {
        mx.set(0.5);
        my.set(0.5);
      }}
      className={`group relative overflow-hidden rounded-[28px] border border-white/[0.07] bg-[rgba(8,12,22,0.55)] p-6 backdrop-blur-3xl transition-colors duration-500 hover:border-white/15 sm:p-7 md:p-6 ${className}`}
    >
      {/* top edge highlight */}
      <div
        className="pointer-events-none absolute inset-x-10 top-0 h-px opacity-60"
        style={{
          background: `linear-gradient(90deg,transparent,${accent}AA,transparent)`,
        }}
      />
      {/* cursor-following glow */}
      <motion.div
        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        style={{ background: glow }}
      />
      <div className="relative z-10">
        {children}
      </div>
    </motion.div>
  );
}

function BentoIcon({
  icon: Icon,
  accent,
  label,
}: {
  icon: typeof Sparkles;
  accent: string;
  label?: string;
}) {
  return (
    <div
      className="relative mb-4 inline-flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-xl"
      style={{
        boxShadow: `0 0 30px -8px ${accent}66, inset 0 1px 0 0 rgba(255,255,255,0.08)`,
      }}
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      <Icon size={18} strokeWidth={1.8} style={{ color: accent }} aria-hidden="true" />
    </div>
  );
}

function Bento() {
  return (
    <section id="O nas" className="mx-auto max-w-7xl px-4 py-24 sm:px-6 md:py-28 lg:px-12 xl:px-16">
      <div className="mx-auto max-w-3xl text-center">
        <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[11px] font-medium tracking-wide text-slate-300 backdrop-blur-xl">
          <span className="h-1.5 w-1.5 rounded-full bg-[#00F5A0]" />
          Co robimy
        </span>
        <h2 className="mt-5 text-[clamp(2rem,6vw,4rem)] font-semibold leading-[1.05] tracking-[-0.03em] text-white">
          Cztery rzeczy, w których jesteśmy{" "}
          <span
            className="bg-clip-text text-transparent"
            style={{
              backgroundImage:
                "linear-gradient(110deg,#FFFFFF 0%,#00F5A0 50%,#6366F1 100%)",
            }}
          >
            bardzo dobrzy
          </span>
          .
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base">
          Bez wodolejstwa. Konkretne usługi, które przekładają się na konkretne wyniki w Twojej firmie.
        </p>
      </div>

      <div className="mt-12 grid grid-cols-1 gap-4 sm:gap-5 md:mt-16 md:auto-rows-fr md:grid-cols-12">
        {/* HERO CARD — Sklepy */}
        <BentoCard
          accent="#00F5A0"
          className="md:col-span-7 min-h-[360px] md:min-h-0 flex flex-col"
        >
          {/* animated 3D grid horizon */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[70%] overflow-hidden">
            <div
              className="absolute inset-x-[-20%] bottom-[-40%] h-[220%]"
              style={{
                background:
                  "repeating-linear-gradient(90deg, rgba(0,245,160,0.35) 0 1px, transparent 1px 56px), repeating-linear-gradient(0deg, rgba(0,245,160,0.35) 0 1px, transparent 1px 56px)",
                transform: "perspective(700px) rotateX(68deg)",
                maskImage:
                  "linear-gradient(to top, black 20%, transparent 75%)",
                WebkitMaskImage:
                  "linear-gradient(to top, black 20%, transparent 75%)",
              }}
            />
            <motion.div
              className="absolute left-1/2 bottom-[20%] h-32 w-32 -translate-x-1/2 rounded-full"
              style={{
                background:
                  "radial-gradient(circle,#00F5A088,transparent 60%)",
                filter: "blur(20px)",
              }}
              animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.9, 0.5] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>

          {/* floating chips */}
          <div className="pointer-events-none absolute right-5 top-5 hidden flex-col items-end gap-2 sm:flex">
            {["Płatności", "Booksy", "Faktury"].map((c, i) => (
              <motion.span
                key={c}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 + i * 0.15, duration: 0.6, ease: EASE }}
                className="rounded-full border border-white/10 bg-white/[0.06] px-3 py-1 text-[10px] font-medium text-slate-200 backdrop-blur-xl"
              >
                {c}
              </motion.span>
            ))}
          </div>

          <BentoIcon icon={ShoppingBag} accent="#00F5A0" label="Ikona sklepu e-commerce z integracjami płatności" />
          <h3 className="mb-3 text-[clamp(1.5rem,3vw,1.9rem)] font-semibold leading-[1.1] tracking-tight text-white">
            Sklepy online, które sprzedają
          </h3>
          <p className="max-w-md text-sm leading-relaxed text-slate-400">
            Pełna integracja płatności, Booksy, automatyczne faktury. Sklepy, które kończą zakup, a nie tylko go pokazują.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {[
              { icon: CreditCard, label: "Stripe · Przelewy24" },
              { icon: Calendar, label: "Booksy" },
              { icon: FileText, label: "Auto-faktury" },
              { icon: ShoppingBag, label: "Shopify · WooCommerce" },
            ].map(({ icon: I, label }) => (
              <span
                key={label}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[11px] font-medium text-slate-300 backdrop-blur-xl"
              >
                <I size={12} className="text-[#00F5A0]" strokeWidth={2} />
                {label}
              </span>
            ))}
          </div>
        </BentoCard>

        {/* B2B */}
        <BentoCard
          accent="#6366F1"
          className="md:col-span-5 min-h-[220px] overflow-hidden"
        >
          {/* stacked cards visual */}
          <div className="pointer-events-none absolute -right-6 top-6 hidden md:block">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 0.6 - i * 0.15, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.6 }}
                className="absolute h-14 w-28 rounded-xl border border-white/10 bg-white/[0.03] backdrop-blur-xl"
                style={{ top: i * 14, right: i * 10 }}
              />
            ))}
          </div>
          <BentoIcon icon={Sparkles} accent="#6366F1" label="Ikona profesjonalnej strony B2B i wizytówki firmowej" />
          <h3 className="mb-2 text-lg font-semibold tracking-tight text-white sm:text-xl">
            Wizytówki i strony B2B
          </h3>
          <p className="text-sm leading-relaxed text-slate-400">
            Profesjonalna twarz Twojej firmy. Oferta, kontakt i dowody zaufania w zasięgu jednego scrolla.
          </p>
          <ul className="mt-5 space-y-2">
            {["Oferta i cennik", "Formularz kontaktowy", "Opinie i case studies"].map((t) => (
              <li key={t} className="flex items-center gap-2 text-[12px] text-slate-300">
                <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-[#6366F1]/40 bg-[#6366F1]/15">
                  <Check size={10} className="text-[#A5B4FC]" strokeWidth={3} />
                </span>
                {t}
              </li>
            ))}
          </ul>
        </BentoCard>

        {/* SEO */}
        <BentoCard
          accent="#00F5A0"
          className="md:col-span-5 min-h-[220px] overflow-hidden"
        >
          <BentoIcon icon={Search} accent="#00F5A0" label="Ikona optymalizacji SEO i widoczności w Google" />
          <h3 className="mb-2 text-lg font-semibold tracking-tight text-white sm:text-xl">
            Widoczność w Google
          </h3>
          <p className="text-sm leading-relaxed text-slate-400">
            Struktura, słowa kluczowe i szybkość. SEO zaszywamy w stronę od pierwszego dnia.
          </p>
          <div className="mt-5 space-y-2">
            {[
              { kw: "fotograf kraków", pos: "#1" },
              { kw: "kwiaciarnia online", pos: "#2" },
              { kw: "kancelaria warszawa", pos: "#3" },
            ].map(({ kw, pos }) => (
              <div
                key={kw}
                className="flex items-center justify-between rounded-lg border border-white/[0.06] bg-white/[0.02] px-2.5 py-1.5 text-[11px]"
              >
                <span className="truncate text-slate-300">{kw}</span>
                <span className="ml-2 inline-flex items-center gap-1 font-semibold text-[#00F5A0]">
                  <TrendingUp size={11} strokeWidth={2.5} />
                  {pos}
                </span>
              </div>
            ))}
          </div>
          <svg viewBox="0 0 100 30" className="mt-4 h-10 w-full overflow-visible">
            <defs>
              <linearGradient id="seoLine" x1="0" x2="1" y1="0" y2="0">
                <stop offset="0%" stopColor="#00F5A0" stopOpacity="0.2" />
                <stop offset="100%" stopColor="#00F5A0" stopOpacity="1" />
              </linearGradient>
              <linearGradient id="seoFill" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#00F5A0" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#00F5A0" stopOpacity="0" />
              </linearGradient>
            </defs>
            <motion.path
              d="M0,26 L20,20 L40,22 L60,12 L80,14 L100,3 L100,30 L0,30 Z"
              fill="url(#seoFill)"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6, duration: 0.8 }}
            />
            <motion.path
              d="M0,26 L20,20 L40,22 L60,12 L80,14 L100,3"
              fill="none"
              stroke="url(#seoLine)"
              strokeWidth="1.5"
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.4, ease: EASE }}
            />
            <motion.circle
              cx="100"
              cy="3"
              r="2.2"
              fill="#00F5A0"
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 1.4, duration: 0.4 }}
            />
          </svg>
        </BentoCard>

        {/* PAGESPEED — full width */}
        <BentoCard
          accent="#00F5A0"
          className="md:col-span-7 min-h-[200px] flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="min-w-0 flex-1">
            <BentoIcon icon={Gauge} accent="#00F5A0" label="Ikona błyskawicznej prędkości PageSpeed 100/100" />
            <h3 className="mb-2 text-lg font-semibold tracking-tight text-white sm:text-xl">
              Błyskawiczna szybkość ładowania
            </h3>
            <p className="max-w-xl text-sm leading-relaxed text-slate-400 md:text-base">
              Optymalizujemy każdy obraz, font i skrypt. Strona, która ładuje się sekundę dłużej, traci 11% użytkowników. My tego nie robimy.
            </p>
            <div className="mt-6 grid max-w-lg grid-cols-3 gap-3">
              {[
                { label: "FCP", value: "0.6s" },
                { label: "LCP", value: "1.1s" },
                { label: "CLS", value: "0.01" },
              ].map(({ label, value }) => (
                <div
                  key={label}
                  className="rounded-xl border border-white/[0.07] bg-white/[0.03] px-3 py-2.5 backdrop-blur-xl"
                >
                  <div className="text-[9px] uppercase tracking-[0.2em] text-slate-400">{label}</div>
                  <div className="mt-1 text-lg font-semibold tabular-nums text-white">{value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* circular gauge */}
          <div className="relative flex shrink-0 items-center justify-center">
            <svg width="128" height="128" viewBox="0 0 160 160" className="-rotate-90">
              <circle
                cx="80"
                cy="80"
                r="68"
                fill="none"
                stroke="rgba(255,255,255,0.06)"
                strokeWidth="6"
              />
              <motion.circle
                cx="80"
                cy="80"
                r="68"
                fill="none"
                stroke="url(#gaugeGrad)"
                strokeWidth="6"
                strokeLinecap="round"
                strokeDasharray={2 * Math.PI * 68}
                initial={{ strokeDashoffset: 2 * Math.PI * 68 }}
                whileInView={{ strokeDashoffset: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 2, ease: EASE }}
              />
              <defs>
                <linearGradient id="gaugeGrad" x1="0" x2="1" y1="0" y2="1">
                  <stop offset="0%" stopColor="#00F5A0" />
                  <stop offset="100%" stopColor="#6366F1" />
                </linearGradient>
              </defs>
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span
                className="text-5xl font-semibold leading-none tracking-tight text-white tabular-nums"
                style={{ textShadow: "0 0 30px rgba(0,245,160,0.45)" }}
              >
                100
              </span>
              <span className="mt-2 text-[9px] uppercase tracking-[0.3em] text-slate-400">
                PageSpeed
              </span>
            </div>
          </div>
        </BentoCard>
      </div>
    </section>
  );
}

/* -------------------- SCROLL TUNNEL -------------------- */
const STAGES = [
  {
    title: "Pierwszy kontakt",
    desc: "Zostawiasz kontakt w formularzu albo dzwonimy do Ciebie sami. 15 minut rozmowy o Twoim biznesie i wiemy, czego potrzeba.",
    icon: PhoneCall,
    color: "#00F5A0",
    tag: "Rozmowa",
  },
  {
    title: "Działające demo w 72 h",
    desc: "Budujemy realny prototyp Twojej strony. Klikalny, responsywny, gotowy do oceny. Bez opłat, bez zobowiązań.",
    icon: LayoutTemplate,
    color: "#6366F1",
    tag: "Prototyp",
  },
  {
    title: "Decyzja i pakiet",
    desc: "Demo Ci się podoba — wybierasz pakiet. Nie podoba — rozstajemy się bez faktur. Tak po prostu.",
    icon: CheckCircle,
    color: "#00F5A0",
    tag: "Wybór",
  },
  {
    title: "Publikacja i opieka",
    desc: "Stawiamy stronę pod Twoją domeną. Opcjonalna opieka techniczna trzyma ją szybką, aktualną i bezpieczną.",
    icon: Rocket,
    color: "#6366F1",
    tag: "Launch",
  },
];

function TunnelCard({
  stage,
  idx,
  progress,
}: {
  stage: (typeof STAGES)[number];
  idx: number;
  progress: ReturnType<typeof useScroll>["scrollYProgress"];
}) {
  // Each card owns a slot. Fade strongly in the middle of the slot,
  // gently overlap on edges so something is always on screen.
  const slot = 1 / STAGES.length;
  const start = idx * slot;
  const end = start + slot;
  const mid = start + slot * 0.5;
  // Use functional transformer form to avoid framer-motion's accelerated
  // WAAPI path which interprets the input range as animation offsets.
  const opacity = useTransform(progress, (v) => {
    const fade = slot * 0.22;
    const holdStart = start + slot * 0.12;
    const holdEnd = end - slot * 0.12;
    if (idx === 0 && v <= holdStart) return 1;
    if (idx === STAGES.length - 1 && v >= holdEnd) return 1;
    if (v >= holdStart && v <= holdEnd) return 1;
    if (v < holdStart && v >= holdStart - fade) return (v - (holdStart - fade)) / fade;
    if (v > holdEnd && v <= holdEnd + fade) return (holdEnd + fade - v) / fade;
    return 0;
  });
  const scale = useTransform(progress, (v) => {
    if (idx === 0 && v <= start) return 1;
    if (v <= start) return 0.96;
    if (v >= end) return 1.04;
    const t = (v - start) / slot;
    return t < 0.5 ? 0.96 + (1 - 0.96) * (t * 2) : 1 + (1.04 - 1) * ((t - 0.5) * 2);
  });
  const y = useTransform(progress, (v) => {
    if (idx === 0 && v <= start) return 0;
    if (v <= start) return 26;
    if (v >= end) return -30;
    const t = (v - start) / slot;
    return 30 + (-30 - 30) * t;
  });

  return (
    <motion.div
      data-tunnel-card
      style={{ scale, opacity, y, willChange: "transform, opacity" }}
      className="pointer-events-none absolute left-1/2 top-[52%] z-10 flex w-[calc(100vw-32px)] max-w-2xl -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center overflow-hidden rounded-3xl border border-white/10 bg-[#05070c]/85 px-5 py-10 shadow-2xl shadow-black/40 backdrop-blur-xl sm:w-[88vw] md:top-1/2 md:w-[55vw] md:px-8 md:py-14"
    >
      {/* ambient color wash tied to stage */}
      <div
        className="pointer-events-none absolute -top-24 left-1/2 h-64 w-64 -translate-x-1/2 rounded-full opacity-50 blur-3xl"
        style={{ background: `radial-gradient(circle, ${stage.color}55, transparent 70%)` }}
      />
      <div className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

      {/* icon medallion */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        whileInView={{ scale: 1, opacity: 1 }}
        viewport={{ once: false, margin: "-20%" }}
        transition={{ duration: 0.6, ease: EASE }}
        className="relative mb-6 flex h-16 w-16 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl md:h-20 md:w-20"
        style={{ boxShadow: `0 0 40px -8px ${stage.color}66, inset 0 1px 0 0 rgba(255,255,255,0.1)` }}
      >
        <stage.icon size={28} style={{ color: stage.color }} strokeWidth={1.8} />
        <span
          className="absolute -bottom-2 -right-2 flex h-7 w-7 items-center justify-center rounded-full border border-white/10 bg-[#05070c] text-[10px] font-bold tabular-nums text-white"
        >
          0{idx + 1}
        </span>
      </motion.div>

      <span
        className="mb-4 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.3em] backdrop-blur-xl"
        style={{
          color: stage.color,
          borderColor: `${stage.color}40`,
          background: `${stage.color}10`,
        }}
      >
        <span className="h-1 w-1 rounded-full" style={{ background: stage.color }} />
        {stage.tag}
      </span>

      <h3 className="bg-gradient-to-b from-white to-slate-500 bg-clip-text text-center text-3xl font-black tracking-tight text-transparent md:text-5xl">
        {stage.title}
      </h3>
      <p className="mt-4 max-w-lg text-center text-base leading-relaxed text-slate-300 md:text-lg">
        {stage.desc}
      </p>
    </motion.div>
  );
}

function ScrollTunnel() {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const pct = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  const activeIdx = useTransform(scrollYProgress, (v) =>
    Math.min(STAGES.length - 1, Math.max(0, Math.floor(v * STAGES.length))),
  );

  return (
    <section id="Proces" ref={ref} className="relative h-[400vh] bg-black">
      <div className="sticky top-0 flex h-[100dvh] w-full items-center justify-center overflow-hidden">
        <div className="absolute left-0 right-0 top-24 z-20 flex flex-col items-center gap-3 px-4 text-center md:top-28">
          <span className="text-xs uppercase tracking-[0.4em] text-[#00F5A0]">
            Jak pracujemy
          </span>
          <h2 className="max-w-2xl text-2xl font-black tracking-tight text-white md:text-4xl">
            Od telefonu do publikacji — w prostych czterech krokach.
          </h2>
          <p className="max-w-md text-sm text-slate-400">
            Bez ofert PDF, bez tygodni czekania na wycenę.
          </p>
        </div>
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="pointer-events-none absolute rounded-full border border-white/5"
            style={{ width: `${40 + i * 25}vw`, height: `${40 + i * 25}vw` }}
            animate={{ rotate: i % 2 ? -360 : 360 }}
            transition={{ duration: 60 + i * 20, repeat: Infinity, ease: "linear" }}
          />
        ))}
        <div className="pointer-events-none absolute h-[40vw] w-[40vw] rounded-full bg-[#00F5A0]/10 blur-3xl" />
        {/* liquid glass core */}
        <motion.div
          className="pointer-events-none absolute h-[60vw] w-[60vw] max-h-[700px] max-w-[700px] rounded-full border border-white/5"
          style={{
            background:
              "conic-gradient(from 0deg, rgba(0,245,160,0.08), transparent 30%, rgba(99,102,241,0.08), transparent 70%)",
          }}
          animate={{ rotate: 360 }}
          transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
        />
        {STAGES.map((s, i) => (
          <TunnelCard key={i} stage={s} idx={i} progress={scrollYProgress} />
        ))}
        <div className="absolute bottom-10 left-1/2 z-20 flex w-[80vw] max-w-md -translate-x-1/2 flex-col items-center gap-3">
          <div className="flex w-full items-center justify-between">
            {STAGES.map((_, i) => (
              <StepDot key={i} idx={i} activeIdx={activeIdx} />
            ))}
          </div>
          <div className="h-1 w-full overflow-hidden rounded-full bg-white/10">
            <motion.div className="h-full bg-[#00F5A0]" style={{ width: pct }} />
          </div>
        </div>
      </div>
    </section>
  );
}

function StepDot({
  idx,
  activeIdx,
}: {
  idx: number;
  activeIdx: ReturnType<typeof useScroll>["scrollYProgress"];
}) {
  const [active, setActive] = useState(false);
  useMotionValueEvent(activeIdx, "change", (v) => setActive(v === idx));
  const stage = STAGES[idx];
  const Icon = stage.icon;
  return (
    <div className="flex flex-col items-center gap-1.5">
      <span
        className="flex h-8 w-8 items-center justify-center rounded-full border transition-all duration-300"
        style={{
          borderColor: active ? stage.color : "rgba(255,255,255,0.08)",
          background: active ? `${stage.color}22` : "rgba(255,255,255,0.02)",
          boxShadow: active ? `0 0 16px -2px ${stage.color}88` : "none",
          color: active ? stage.color : "rgba(255,255,255,0.3)",
        }}
      >
        <Icon size={14} strokeWidth={2} />
      </span>
      <span
        className="text-[9px] uppercase tracking-[0.3em] transition-colors"
        style={{ color: active ? stage.color : "rgb(148,163,184)" }}
      >
        0{idx + 1}
      </span>
    </div>
  );
}

/* -------------------- DOMINANCE ENGINE -------------------- */
function DominanceEngine() {
  const [dom, setDom] = useState(false);
  const [destroyed, setDestroyed] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const playerX = useMotionValue(0);
  const playerY = useMotionValue(0);
  const psx = useSpring(playerX, { stiffness: 200, damping: 25 });
  const psy = useSpring(playerY, { stiffness: 200, damping: 25 });
  const [resetKey, setResetKey] = useState(0);

  const dots = useMemo(
    () =>
      Array.from({ length: 50 }).map((_, i) => ({
        x: round3((pseudoRandom(i, resetKey + 4) - 0.5) * 520),
        y: round3((pseudoRandom(i, resetKey + 5) - 0.5) * 280),
      })),
    [resetKey],
  );

  const start = (e: React.PointerEvent) => {
    setDom(true);
    setDestroyed(0);
    update(e);
    setTimeout(() => setDestroyed(100), 1200);
  };
  const end = () => {
    setDom(false);
    playerX.set(0);
    playerY.set(0);
    setTimeout(() => {
      setDestroyed(0);
      setResetKey((k) => k + 1);
    }, 2000);
  };
  const update = (e: React.PointerEvent) => {
    if (!dom && e.type !== "pointerdown") return;
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    playerX.set(e.clientX - r.left - r.width / 2);
    playerY.set(e.clientY - r.top - r.height / 2);
  };

  return (
    <section id="Dominacja" className="mx-auto max-w-6xl overflow-hidden px-4 py-32 sm:px-6 lg:px-12 xl:px-16 md:py-48">
      <span className="mb-4 block text-center text-sm uppercase tracking-[0.4em] text-[#00F5A0]">
        Twoja widoczność
      </span>
      <h2 className="mb-10 text-center text-4xl font-black tracking-tighter text-white md:text-6xl">
        Wyróżnij się w swojej branży.
      </h2>
      <div
        ref={ref}
        onPointerDown={start}
        onPointerUp={end}
        onPointerLeave={end}
        onPointerMove={update}
        className="group relative flex h-[560px] max-h-[70vh] min-h-[420px] w-full select-none touch-none items-center justify-center overflow-hidden rounded-3xl border border-white/10"
        style={{
          cursor: "crosshair",
          background: dom ? "#1A0505" : "#020305",
          boxShadow: "inset 0 0 100px rgba(0,0,0,0.8)",
          transition: "background 0.4s",
        }}
      >
        <motion.div
          className="absolute inset-0 pointer-events-none"
          animate={{ scale: dom ? 1.1 : 1 }}
          transition={{ duration: 0.6 }}
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />

        <span className="pointer-events-none absolute max-w-[90%] text-center text-4xl font-black uppercase tracking-normal text-white/5 md:text-7xl">
          {destroyed >= 100 ? "Jesteś na szczycie" : "Twój rynek"}
        </span>

        <AnimatePresence>
          {dom && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="absolute top-6 z-10 text-sm uppercase tracking-[0.3em] text-[#00F5A0]"
            >
              Twój zasięg: {destroyed}%
            </motion.div>
          )}
        </AnimatePresence>

        {dots.map((d, i) => (
          <motion.span
            key={`${resetKey}-${i}`}
            className="absolute h-3 w-3 rounded-full bg-slate-700"
            initial={{ x: d.x, y: d.y, opacity: 1 }}
            animate={
              dom
                ? { x: 0, y: 0, opacity: 0, scale: 0.2 }
                : {
                    x: [d.x, d.x + 20, d.x - 20, d.x],
                    y: [d.y, d.y - 10, d.y + 10, d.y],
                    opacity: 1,
                  }
            }
            transition={
              dom
                ? { duration: 1, ease: EASE, delay: i * 0.01 }
                : { duration: 8 + (i % 5), repeat: Infinity, ease: "easeInOut" }
            }
          />
        ))}

        <motion.div
          style={{ x: psx, y: psy, willChange: "transform" }}
          animate={{
            scale: dom ? 3 : 1,
            boxShadow: dom
              ? "0 0 150px #00F5A0, 0 0 50px #00F5A0"
              : "0 0 50px #00F5A0",
          }}
          transition={{ type: "spring", stiffness: 100, damping: 20 }}
          className="absolute flex h-16 w-16 items-center justify-center rounded-full bg-[#00F5A0] text-xs font-bold text-black"
        >
          TY
        </motion.div>

        <span className="absolute bottom-6 z-10 text-center text-sm text-slate-500">
          Naciśnij i przytrzymaj, aby zobaczyć efekt naszej współpracy.
        </span>
      </div>
    </section>
  );
}

/* -------------------- PRICING -------------------- */
const PLANS = [
  {
    badge: "Pakiet 01 — Start",
    title: "Klasyczna wizytówka",
    desc: "Dla firm, które potrzebują szybkiej i czytelnej wizytówki online.",
    price: "1 899 zł",
    period: "jednorazowo",
    features: [
      "2 do 3 podstron dedykowanych",
      "Pełne RWD (mobile first)",
      "Optymalizacja struktury SEO",
      "Integracja Map Google i formularza",
    ],
    care: "Opcjonalna opieka: +179 zł miesięcznie — aktualizacje, backupy, bezpieczeństwo.",
    cta: "Wybieram Start",
    highlight: false,
  },
  {
    badge: "Pakiet 02 — Growth",
    title: "Interaktywna platforma",
    desc: "Dla marek, w których strona ma realnie pracować — kalkulować, rezerwować, sprzedawać.",
    price: "2 499 zł",
    period: "jednorazowo",
    features: [
      "Rozbudowana struktura do 7 stron",
      "Zaawansowane interakcje i animacje",
      "Formularze wycen i kalkulatory",
      "Integracja opinii Google Reviews",
    ],
    care: "Opcjonalna opieka: +199 zł miesięcznie — szybkie poprawki i wsparcie do 24 h.",
    cta: "Wybieram Growth",
    highlight: true,
  },
  {
    badge: "Pakiet 03 — Custom",
    title: "Sklep lub system dedykowany",
    desc: "Dla rozbudowanego e-commerce, systemów rezerwacji i integracji szytych na miarę.",
    price: "Wycena indywidualna",
    period: "wg specyfikacji",
    features: [
      "Pełny e-commerce z płatnościami",
      "Systemy rezerwacji (np. Booksy)",
      "Integracje zewnętrznych API",
      "Nielimitowana baza podstron",
    ],
    care: "Dedykowana opieka techniczna z umową SLA — wycena indywidualna.",
    cta: "Porozmawiajmy o wycenie",
    highlight: false,
  },
];

function Pricing() {
  return (
    <section id="Cennik" className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 md:py-40 lg:px-12 xl:px-16">
      <div className="pointer-events-none absolute inset-0 -z-10 [background:radial-gradient(ellipse_60%_50%_at_50%_30%,rgba(0,245,160,0.07),transparent_70%)]" />
      <div className="mx-auto max-w-3xl text-center">
        <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[11px] font-medium tracking-wide text-slate-300 backdrop-blur-xl">
          <span className="h-1.5 w-1.5 rounded-full bg-[#00F5A0]" />
          Cennik
        </span>
        <h2 className="mt-5 text-[clamp(2rem,6vw,4rem)] font-semibold leading-[1.05] tracking-[-0.03em] text-white">
          Wybierz pakiet pod{" "}
          <span
            className="bg-clip-text text-transparent"
            style={{
              backgroundImage:
                "linear-gradient(110deg,#FFFFFF 0%,#00F5A0 50%,#6366F1 100%)",
            }}
          >
            swój biznes
          </span>
          .
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base">
          Zawsze zaczynamy od bezpłatnego demo. Cena stała, ustalana przed startem. Płacisz dopiero, gdy projekt Ci się spodoba.
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: EASE }}
        className="mt-12 grid grid-cols-1 items-stretch gap-5 sm:gap-6 md:mt-16 lg:grid-cols-3"
      >
        {PLANS.map((p) => (
          <div
            key={p.badge}
            className={`group relative flex min-w-0 flex-col overflow-hidden rounded-[28px] p-[1px] transition-all duration-500 ${
              p.highlight ? "lg:-translate-y-3" : "hover:-translate-y-1"
            }`}
            style={
              p.highlight
                ? {
                    background:
                      "linear-gradient(140deg,rgba(0,245,160,0.55),rgba(99,102,241,0.35) 50%,rgba(255,255,255,0.05))",
                  }
                : {
                    background:
                      "linear-gradient(140deg,rgba(255,255,255,0.10),rgba(255,255,255,0.02))",
                  }
            }
          >
            <div
              className={`relative flex h-full min-w-0 flex-col overflow-hidden rounded-[27px] p-6 sm:p-7 md:p-8 ${
                p.highlight ? "bg-[#04070d]" : "bg-[#04070d]/85 backdrop-blur-xl"
              }`}
            >
              {p.highlight && (
                <>
                  <div className="pointer-events-none absolute -left-20 -top-20 h-56 w-56 rounded-full bg-[#00F5A0]/20 blur-3xl" />
                  <div className="pointer-events-none absolute -right-20 -bottom-24 h-60 w-60 rounded-full bg-[#6366F1]/20 blur-3xl" />
                  <motion.span
                    className="pointer-events-none absolute -top-1 left-0 h-[2px] w-1/3 bg-gradient-to-r from-transparent via-white to-transparent"
                    animate={{ x: ["-100%", "400%"] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                  />
                </>
              )}

              {/* header row */}
              <div className="relative z-10 flex items-start justify-between gap-3">
                <span
                  className={`text-[10px] font-semibold uppercase tracking-[0.25em] ${
                    p.highlight ? "text-[#00F5A0]" : "text-slate-500"
                  }`}
                >
                  {p.badge}
                </span>
                {p.highlight && (
                  <span className="shrink-0 rounded-full border border-[#00F5A0]/40 bg-[#00F5A0]/10 px-2.5 py-1 text-[9px] font-bold uppercase tracking-widest text-[#00F5A0]">
                    Polecane
                  </span>
                )}
              </div>

              <h3 className="relative z-10 mt-3 text-2xl font-semibold tracking-tight text-white sm:text-[26px]">
                {p.title}
              </h3>
              <p className="relative z-10 mt-2 text-sm leading-relaxed text-slate-400">
                {p.desc}
              </p>

              {/* price */}
              <div className="relative z-10 mt-6 flex items-baseline gap-2">
                <p className="text-[clamp(1.9rem,7vw,2.6rem)] font-semibold leading-none tracking-tight tabular-nums text-white">
                  {p.price}
                </p>
              </div>
              <p className="relative z-10 mt-1.5 text-[11px] uppercase tracking-[0.2em] text-slate-500">
                {p.period}
              </p>

              <div className="relative z-10 my-6 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

              <ul className="relative z-10 flex flex-col gap-2.5">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300">
                    <span
                      className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${
                        p.highlight
                          ? "bg-[#00F5A0]/15 text-[#00F5A0]"
                          : "bg-white/[0.06] text-slate-400"
                      }`}
                    >
                      <CheckCircle size={11} strokeWidth={2.4} />
                    </span>
                    <span className="min-w-0 leading-snug">{f}</span>
                  </li>
                ))}
              </ul>

              <div className="relative z-10 mt-6 rounded-2xl border border-white/[0.08] bg-white/[0.02] p-3.5 text-[12px] leading-relaxed text-slate-400">
                {p.care}
              </div>

              <a
                href="#cta"
                className={`relative z-10 mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-semibold transition active:scale-[0.98] ${
                  p.highlight
                    ? "bg-white text-black hover:scale-[1.02]"
                    : "border border-white/15 bg-white/[0.03] text-white backdrop-blur-xl hover:bg-white/[0.08]"
                }`}
              >
                {p.cta}
                <ArrowRight size={14} />
              </a>
            </div>
          </div>
        ))}
      </motion.div>
    </section>
  );
}

/* -------------------- ADD-ONS -------------------- */
const ADDONS = [
  {
    title: "Kalendarz Google i rezerwacje",
    desc: "Automatyczne umawianie terminów online.",
    price: "od 399 zł",
  },
  {
    title: "Widżet Google Reviews Live",
    desc: "Opinie z profilu Google zaciągane na żywo.",
    price: "od 299 zł",
  },
  {
    title: "Płatności online (Stripe lub Przelewy24)",
    desc: "Konfiguracja bramki i integracja ze stroną.",
    price: "od 499 zł",
  },
  {
    title: "Wielojęzyczność strony",
    desc: "Dodatkowa wersja językowa z pełnym tłumaczeniem.",
    price: "od 499 zł za język",
  },
  {
    title: "Blog z systemem CMS",
    desc: "Panel do publikowania artykułów bez kodowania.",
    price: "od 699 zł",
  },
  {
    title: "Newsletter i automatyzacja",
    desc: "Formularz zapisu, integracja z MailerLite lub Brevo.",
    price: "od 599 zł",
  },
];

function AddOns() {
  const [open, setOpen] = useState(false);
  return (
    <section id="Dodatki" className="mx-auto max-w-4xl px-4 pb-20 sm:px-6 lg:px-12 md:pb-28">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, ease: EASE }}
        className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-xl"
      >
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
        <button
          onClick={() => setOpen((o) => !o)}
          className="flex w-full items-center justify-between gap-4 px-5 py-5 text-left transition hover:bg-white/[0.02] sm:px-7 sm:py-6"
          aria-expanded={open}
        >
          <div className="flex min-w-0 items-center gap-4">
            <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-[#00F5A0]/30 bg-[#00F5A0]/10 text-[#00F5A0]">
              <Zap size={18} />
            </div>
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-[0.25em] text-slate-500">Dodatki opcjonalne</p>
              <h3 className="text-lg font-black text-white sm:text-xl">
                Cennik integracji i rozszerzeń
              </h3>
            </div>
          </div>
          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-white transition">
            {open ? <Minus size={16} /> : <Plus size={16} />}
          </span>
        </button>
        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              key="addons"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.4, ease: EASE }}
              className="overflow-hidden"
            >
              <div className="border-t border-white/5">
                {ADDONS.map((a, i) => (
                  <div
                    key={a.title}
                    className={`flex items-start gap-4 px-5 py-4 sm:items-center sm:px-7 ${
                      i === 0 ? "" : "border-t border-white/5"
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-white sm:text-base">{a.title}</p>
                      <p className="mt-0.5 text-xs text-slate-400 sm:text-sm">{a.desc}</p>
                    </div>
                    <span className="shrink-0 text-right text-sm font-black tabular-nums text-[#00F5A0] sm:text-base">
                      {a.price}
                    </span>
                  </div>
                ))}
                <div className="border-t border-white/5 px-5 py-4 text-center text-xs text-slate-500 sm:px-7">
                  Każdy dodatek wyceniamy indywidualnie po zapoznaniu się z Twoimi potrzebami.
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </section>
  );
}

/* -------------------- MARKETING CTA -------------------- */
function MarketingCTA() {
  return (
    <section className="relative mx-auto max-w-5xl px-4 pb-28 sm:px-6 lg:px-12 md:pb-40">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#04110b] via-[#02050A] to-[#020305] p-7 backdrop-blur-xl md:p-12"
        style={{ boxShadow: "0 0 80px rgba(0,245,160,0.1)" }}
      >
        <motion.span
          className="pointer-events-none absolute -left-1/3 top-0 h-[200%] w-1/2 rotate-12 bg-[radial-gradient(ellipse_at_center,rgba(0,245,160,0.18),transparent_70%)] blur-2xl"
          animate={{ x: ["-20%", "240%"] }}
          transition={{ duration: 11, repeat: Infinity, ease: "linear" }}
        />
        <div className="relative z-10 flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <span className="mb-3 inline-block text-xs uppercase tracking-[0.3em] text-[#00F5A0]">
              Nowość — marketing i social media
            </span>
            <h2 className="text-2xl font-black leading-tight text-white md:text-4xl">
              Strona to dopiero początek. Zapraszamy na nią ludzi.
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-400 md:text-base">
              Pakiety prowadzenia social media i kampanii Meta Ads. Pełny cennik znajdziesz na osobnej podstronie.
            </p>
          </div>
          <Link
            to="/marketing"
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-[#00F5A0] px-7 py-4 text-sm font-black uppercase tracking-wider text-black shadow-lg shadow-[#00F5A0]/40 transition hover:brightness-125"
          >
            Zobacz cennik marketingu
            <ArrowRight size={16} />
          </Link>
        </div>
      </motion.div>
    </section>
  );
}

/* -------------------- EVENT HORIZON FOOTER -------------------- */
function EventHorizon() {
  return <EventHorizonInner />;
}

/* -------------------- DEMO TEASER -------------------- */
const DEMO_PREVIEW = [
  { label: "Burze live", gradient: "from-[#06B6D4] to-[#A21CAF]" },
  { label: "Gastronomia", gradient: "from-[#FF7E5F] to-[#FEB47B]" },
  { label: "Prawo", gradient: "from-[#0F2027] to-[#2C5364]" },
  { label: "Fitness", gradient: "from-[#FF512F] to-[#F09819]" },
  { label: "Beauty", gradient: "from-[#FBC2EB] to-[#A18CD1]" },
  { label: "E-commerce", gradient: "from-[#00F5A0] to-[#00D9F5]" },
  { label: "Nieruchomości", gradient: "from-[#6366F1] to-[#8B5CF6]" },
];

function DemoTeaser() {
  return (
    <section id="realizacje" className="relative z-20 mx-auto w-full max-w-7xl px-4 pb-20 pt-8 sm:px-6 lg:px-12 xl:px-16">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: EASE }}
        className="relative overflow-hidden rounded-[32px] border border-white/10 bg-gradient-to-br from-white/[0.05] via-white/[0.02] to-transparent p-8 backdrop-blur-2xl md:p-14"
      >
        {/* ambient glows */}
        <div className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-[#00F5A0]/15 blur-[100px]" />
        <div className="pointer-events-none absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-[#6366F1]/15 blur-[100px]" />
        <div className="pointer-events-none absolute inset-x-12 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />

        <div className="relative grid items-center gap-12 lg:grid-cols-[1.05fr_1fr]">
          {/* Left: copy */}
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-300 backdrop-blur-xl">
              <Sparkles size={12} className="text-[#00F5A0]" />
              Nasze realizacje
            </span>
            <h2 className="mt-5 text-balance text-4xl font-black leading-[1.02] tracking-tight text-white md:text-5xl lg:text-[3.4rem]">
              Zobacz{" "}
              <span
                className="bg-clip-text text-transparent"
                style={{
                  backgroundImage: "linear-gradient(110deg,#00F5A0 0%,#6366F1 100%)",
                }}
              >
                demo stron
              </span>{" "}
              dla różnych branż.
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-400 md:text-lg">
              Restauracje, kancelarie, salony, sklepy, deweloperzy. Każde demo to
              gotowy punkt startowy — kliknij, sprawdź jak działa, a my dopasujemy je
              pod Twoją markę w 72 godziny.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/realizacje"
                className="group relative inline-flex items-center gap-2 overflow-hidden rounded-full bg-white px-6 py-3.5 text-sm font-bold text-black transition-transform duration-300 hover:scale-[1.02]"
              >
                <LayoutTemplate size={16} className="relative z-10" />
                <span className="relative z-10">Zobacz wszystkie realizacje</span>
                <ArrowRight
                  size={16}
                  className="relative z-10 transition-transform duration-300 group-hover:translate-x-0.5"
                />
                <span className="absolute inset-0 -z-0 rounded-full bg-[#00F5A0] opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-70" />
              </Link>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#00F5A0] opacity-60" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-[#00F5A0]" />
                </span>
                <span>
                  <span className="font-semibold text-slate-200">6 demo</span> dostępnych
                  do podglądu
                </span>
              </div>
            </div>
          </div>

          {/* Right: stacked preview cards */}
          <div className="relative h-[320px] md:h-[380px]">
            {DEMO_PREVIEW.map((d, i) => {
              const offset = i - (DEMO_PREVIEW.length - 1) / 2;
              return (
                <motion.div
                  key={d.label}
                  initial={{ opacity: 0, y: 30, rotate: offset * 3 }}
                  whileInView={{ opacity: 1, y: 0, rotate: offset * 3 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, ease: EASE, delay: 0.1 + i * 0.07 }}
                  whileHover={{ y: -8, rotate: offset * 3, scale: 1.03, zIndex: 50 }}
                  className="absolute left-1/2 top-1/2 h-[210px] w-[260px] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl border border-white/15 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.7)] backdrop-blur-xl"
                  style={{
                    transform: `translate(calc(-50% + ${offset * 38}px), calc(-50% + ${
                      Math.abs(offset) * 6
                    }px))`,
                    zIndex: 10 + i,
                  }}
                >
                  <div className={`relative h-full w-full bg-gradient-to-br ${d.gradient}`}>
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_20%,rgba(255,255,255,0.3),transparent_55%)]" />
                    {/* mock browser bar */}
                    <div className="absolute inset-x-3 top-3 flex h-5 items-center gap-1 rounded-md bg-black/30 px-2 backdrop-blur-md">
                      <span className="h-1.5 w-1.5 rounded-full bg-white/50" />
                      <span className="h-1.5 w-1.5 rounded-full bg-white/50" />
                      <span className="h-1.5 w-1.5 rounded-full bg-white/50" />
                    </div>
                    {/* mock content */}
                    <div className="absolute inset-x-4 bottom-4 space-y-1.5">
                      <div className="h-2 w-2/3 rounded-full bg-white/40" />
                      <div className="h-2 w-1/2 rounded-full bg-white/25" />
                    </div>
                    {/* label */}
                    <div className="absolute left-4 top-12 rounded-full bg-black/35 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white backdrop-blur-md">
                      {d.label}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.div>
    </section>
  );
}

function SiteFooter() {
  const linkGroups = [
    {
      title: "Oferta",
      links: [
        { label: "Co robimy", href: "#O nas" },
        { label: "Proces", href: "#Proces" },
        { label: "Cennik", href: "#Cennik" },
        { label: "Dodatki", href: "#Dodatki" },
      ],
    },
    {
      title: "Firma",
      links: [
        { label: "Marketing", to: "/marketing" as const },
        { label: "Kontakt", href: "#cta" },
      ],
    },
    {
      title: "Dokumenty",
      links: [
        { label: "Polityka prywatności", to: "/polityka-prywatnosci" as const },
        { label: "Regulamin", to: "/regulamin" as const },
        { label: "Polityka cookies", to: "/cookies" as const },
      ],
    },
  ];

  return (
    <footer className="relative z-20 mx-auto w-full max-w-7xl px-4 pb-10 sm:px-6 lg:px-12 xl:px-16 pt-4">
      <div
        className="relative overflow-hidden rounded-[28px] p-[1px]"
        style={{
          background:
            "linear-gradient(140deg,rgba(0,245,160,0.35),rgba(99,102,241,0.25) 50%,rgba(255,255,255,0.05))",
        }}
      >
        <div className="relative overflow-hidden rounded-[27px] bg-[#04070d]/85 px-6 py-10 backdrop-blur-2xl sm:px-10 sm:py-12">
          <div className="pointer-events-none absolute -left-24 -bottom-24 h-72 w-72 rounded-full bg-[#00F5A0]/12 blur-3xl" />
          <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#6366F1]/12 blur-3xl" />

          <div className="relative grid gap-10 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]">
            {/* Brand */}
            <div className="min-w-0">
              <Logo size={28} />
              <p className="mt-5 max-w-sm text-sm leading-relaxed text-slate-400">
                Projektujemy, wdrażamy i rozwijamy strony, które są szybkie, czytelne i gotowe na wzrost Twojej firmy.
              </p>
              <a
                href="#cta"
                className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-xs font-semibold text-white backdrop-blur-xl transition hover:bg-white/[0.08]"
              >
                Umów darmowe demo
                <ArrowRight size={12} />
              </a>
            </div>

            {linkGroups.map((g) => (
              <div key={g.title} className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-500">
                  {g.title}
                </p>
                <ul className="mt-4 flex flex-col gap-2.5">
                  {g.links.map((l) =>
                    "to" in l && l.to ? (
                      <li key={l.label}>
                        <Link
                          to={l.to}
                          className="group inline-flex items-center gap-1.5 text-sm text-slate-400 transition hover:text-white"
                        >
                          {l.label}
                          <ArrowRight size={12} className="opacity-0 transition group-hover:opacity-100" />
                        </Link>
                      </li>
                    ) : (
                      <li key={l.label}>
                        <a
                          href={l.href!}
                          className="group inline-flex items-center gap-1.5 text-sm text-slate-400 transition hover:text-white"
                        >
                          {l.label}
                          <ArrowRight size={12} className="opacity-0 transition group-hover:opacity-100" />
                        </a>
                      </li>
                    ),
                  )}
                </ul>
              </div>
            ))}

            {/* Contact + social */}
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-500">
                Kontakt
              </p>
              <ul className="mt-4 flex flex-col gap-2.5">
                <li>
                  <a
                    href="mailto:kontakt@skutecznestrony.com"
                    className="inline-flex items-center gap-2 text-sm text-slate-400 transition hover:text-white"
                  >
                    <Mail size={13} />
                    kontakt@skutecznestrony.com
                  </a>
                </li>
              </ul>
              <div className="mt-5 flex items-center gap-2">
                {[
                  { icon: Instagram, href: "#", label: "Instagram" },
                  { icon: Linkedin, href: "#", label: "LinkedIn" },
                ].map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    aria-label={s.label}
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/[0.03] text-slate-400 transition hover:border-[#00F5A0]/40 hover:bg-white/[0.06] hover:text-[#00F5A0]"
                  >
                    <s.icon size={15} />
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="relative mt-10 flex flex-col gap-3 border-t border-white/[0.06] pt-6 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-slate-500">
              © {new Date().getFullYear()} SkuteczneStrony. Wszelkie prawa zastrzeżone.
            </p>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-500">
              <Link to="/polityka-prywatnosci" className="transition hover:text-slate-300">Polityka prywatności</Link>
              <Link to="/regulamin" className="transition hover:text-slate-300">Regulamin</Link>
              <Link to="/cookies" className="transition hover:text-slate-300">Cookies</Link>
              <span className="inline-flex items-center gap-1.5">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#00F5A0] opacity-60" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#00F5A0]" />
                </span>
                Online — odpowiadamy w kilka godzin
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

function Field({
  icon: Icon,
  name,
  type = "text",
  placeholder,
  required,
  maxLength,
}: {
  icon: typeof Mail;
  name: string;
  type?: string;
  placeholder: string;
  required?: boolean;
  maxLength?: number;
}) {
  return (
    <label className="group relative flex items-center gap-3 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] px-4 transition-all duration-300 focus-within:border-[#00F5A0]/60 focus-within:bg-white/[0.06] focus-within:shadow-[0_0_0_4px_rgba(0,245,160,0.08)]">
      <Icon size={16} className="shrink-0 text-slate-500 transition-colors group-focus-within:text-[#00F5A0]" strokeWidth={1.8} />
      <input
        name={name}
        type={type}
        placeholder={placeholder}
        required={required}
        maxLength={maxLength}
        className="w-full min-w-0 bg-transparent py-4 text-sm text-white placeholder:text-slate-500 outline-none"
      />
    </label>
  );
}

function FieldArea({
  icon: Icon,
  name,
  placeholder,
  rows = 4,
  maxLength,
}: {
  icon: typeof Mail;
  name: string;
  placeholder: string;
  rows?: number;
  maxLength?: number;
}) {
  return (
    <label className="group relative flex gap-3 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 transition-all duration-300 focus-within:border-[#00F5A0]/60 focus-within:bg-white/[0.06] focus-within:shadow-[0_0_0_4px_rgba(0,245,160,0.08)]">
      <Icon size={16} className="mt-1.5 shrink-0 text-slate-500 transition-colors group-focus-within:text-[#00F5A0]" strokeWidth={1.8} />
      <textarea
        name={name}
        rows={rows}
        placeholder={placeholder}
        maxLength={maxLength}
        className="w-full min-w-0 resize-none bg-transparent py-1.5 text-sm text-white placeholder:text-slate-500 outline-none"
      />
    </label>
  );
}

function EventHorizonInner() {
  const skewX = useMotionValue(0);
  const skewY = useMotionValue(0);
  const sx = useSpring(skewX, { stiffness: 50, damping: 25 });
  const sy = useSpring(skewY, { stiffness: 50, damping: 25 });
  const [hov, setHov] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [consent, setConsent] = useState(false);
  const formLoadedAt = useMemo(() => Date.now(), []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia("(pointer: coarse)").matches) return;
    let lastEv: PointerEvent | null = null;
    let raf = 0;
    const flush = () => {
      raf = 0;
      if (!lastEv) return;
      skewX.set(((lastEv.clientX - window.innerWidth / 2) / window.innerWidth) * 10);
      skewY.set(((lastEv.clientY - window.innerHeight / 2) / window.innerHeight) * 6);
    };
    const m = (e: PointerEvent) => {
      lastEv = e;
      if (!raf) raf = requestAnimationFrame(flush);
    };
    window.addEventListener("pointermove", m, { passive: true });
    return () => {
      window.removeEventListener("pointermove", m);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [skewX, skewY]);

  const particles = useMemo(
    () =>
      Array.from({ length: 20 }).map((_, i) => ({
        x: round3((pseudoRandom(i, 8) - 0.5) * 260),
        y: round3((pseudoRandom(i, 9) - 0.5) * 240),
      })),
    [],
  );

  return (
    <section
      id="cta"
      className="relative overflow-hidden bg-black px-4 sm:px-6 lg:px-12 xl:px-16 pb-8 pt-28 md:pt-40"
    >
      <motion.div
        className="pointer-events-none absolute bottom-[-35%] left-1/2 aspect-square w-[140vw] -translate-x-1/2 rounded-full md:w-[90vw]"
        style={{
          background:
            "radial-gradient(circle, rgba(0,245,160,0.25) 0%, transparent 70%)",
          filter: "blur(100px)",
        }}
        animate={{ opacity: [0.6, 1, 0.6], scale: [1, 1.05, 1] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />

      <motion.h2
        className="pointer-events-none absolute left-1/2 top-10 z-0 max-w-[1200px] -translate-x-1/2 select-none whitespace-nowrap text-center text-[18vw] font-black leading-none text-transparent md:text-[10rem]"
        style={{
          WebkitTextStroke: "1.5px rgba(255,255,255,0.05)",
          skewX: sx,
          skewY: sy,
        }}
      >
        START · 72H
      </motion.h2>

      <div className="relative z-20 mx-auto mb-20 w-full max-w-6xl">
        {/* gradient ring wrapper */}
        <div
          className="rounded-[32px] p-[1px]"
          style={{
            background:
              "linear-gradient(140deg,rgba(0,245,160,0.45),rgba(99,102,241,0.35) 50%,rgba(255,255,255,0.06))",
          }}
        >
          <div className="relative overflow-hidden rounded-[31px] bg-[#04070d]/85 backdrop-blur-2xl">
            {/* internal glows */}
            <div className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-[#00F5A0]/15 blur-3xl" />
            <div className="pointer-events-none absolute -right-24 -bottom-24 h-72 w-72 rounded-full bg-[#6366F1]/15 blur-3xl" />
            <motion.span
              className="pointer-events-none absolute -top-px left-0 h-[2px] w-1/3 bg-gradient-to-r from-transparent via-white to-transparent"
              animate={{ x: ["-100%", "400%"] }}
              transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
            />

            <div className="relative grid grid-cols-1 gap-0 md:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
              {/* LEFT — value column */}
              <div className="relative flex flex-col justify-between gap-8 p-6 sm:p-8 md:border-r md:border-white/[0.06] md:p-10">
                <div>
                  <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[11px] font-medium tracking-wide text-slate-300 backdrop-blur-xl">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#00F5A0] opacity-60" />
                      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#00F5A0]" />
                    </span>
                    Kontakt
                  </span>
                  <h3 className="mt-5 text-[clamp(1.8rem,5vw,3rem)] font-semibold leading-[1.05] tracking-[-0.03em] text-white">
                    Porozmawiajmy o{" "}
                    <span
                      className="bg-clip-text text-transparent"
                      style={{
                        backgroundImage:
                          "linear-gradient(110deg,#FFFFFF 0%,#00F5A0 50%,#6366F1 100%)",
                      }}
                    >
                      Twoim projekcie
                    </span>
                    .
                  </h3>
                  <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-400 sm:text-base">
                    Zostaw kontakt — oddzwonimy lub odpiszemy w kilka godzin. Ustalimy szczegóły i w 72 h zobaczysz działający prototyp.
                  </p>
                </div>

                <ul className="flex flex-col gap-3">
                  {[
                    { icon: Clock, t: "Demo w 72 h", d: "Bez ofert PDF i tygodni czekania." },
                    { icon: ShieldCheck, t: "Zero ryzyka", d: "Płacisz dopiero, gdy projekt Ci się spodoba." },
                    { icon: Sparkles, t: "Realny prototyp", d: "Klikalny, responsywny — nie mockup." },
                  ].map((row) => (
                    <li key={row.t} className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] text-[#00F5A0]">
                        <row.icon size={15} strokeWidth={1.9} />
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-white">{row.t}</p>
                        <p className="text-xs leading-relaxed text-slate-400">{row.d}</p>
                      </div>
                    </li>
                  ))}
                </ul>

                <a
                  href="mailto:kontakt@skutecznestrony.com"
                  className="inline-flex items-center gap-2 text-sm text-slate-400 transition hover:text-white"
                >
                  <Mail size={14} />
                  kontakt@skutecznestrony.com
                </a>
              </div>

              {/* RIGHT — form */}
              <div className="relative p-6 sm:p-8 md:p-10">
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (submitting) return;
                    const f = e.currentTarget as HTMLFormElement;
                    const data = new FormData(f);
                    // Anti-spam: honeypot field (must stay empty) + minimum time on form
                    if (((data.get("company_website") as string) ?? "").trim() !== "") {
                      setSent(true);
                      f.reset();
                      return;
                    }
                    if (Date.now() - formLoadedAt < 2500) {
                      toast.error("Chwila, formularz wysłany za szybko. Spróbuj ponownie.");
                      return;
                    }
                    if (!consent) {
                      toast.error("Zaakceptuj politykę prywatności, aby wysłać formularz.");
                      return;
                    }
                    const schema = z.object({
                      name: z.string().trim().min(1, "Podaj imię lub nazwę firmy").max(100),
                      phone: z.string().trim().min(3, "Podaj numer telefonu").max(30),
                      email: z.string().trim().email("Niepoprawny e-mail").max(255),
                      message: z.string().trim().max(2000).optional().or(z.literal("")),
                    });
                    const parsed = schema.safeParse({
                      name: data.get("name"),
                      phone: data.get("phone"),
                      email: data.get("email"),
                      message: data.get("message") ?? "",
                    });
                    if (!parsed.success) {
                      toast.error(parsed.error.issues[0]?.message ?? "Sprawdź dane formularza");
                      return;
                    }
                    setSubmitting(true);
                    const { supabase } = await import("@/integrations/supabase/client");
                    const { error } = await supabase.from("leads").insert({
                      name: parsed.data.name,
                      phone: parsed.data.phone,
                      email: parsed.data.email,
                      message: parsed.data.message || null,
                      source: "website",
                    });
                    setSubmitting(false);
                    if (error) {
                      toast.error("Nie udało się wysłać. Spróbuj ponownie za chwilę.");
                      return;
                    }
                    toast.success("Dziękujemy! Odezwiemy się w kilka godzin.");
                    setSent(true);
                    f.reset();
                  }}
                  className="flex flex-col gap-3.5"
                >
                  {/* Honeypot — ukryte pole anty-spam; prawdziwi użytkownicy go nie wypełnią */}
                  <input
                    type="text"
                    name="company_website"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="absolute left-[-9999px] top-auto h-px w-px opacity-0"
                  />
                  <Field icon={User} name="name" placeholder="Imię lub nazwa firmy" required maxLength={100} />
                  <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                    <Field icon={PhoneCall} name="phone" type="tel" placeholder="Numer telefonu" required maxLength={30} />
                    <Field icon={Mail} name="email" type="email" placeholder="Adres e-mail" required maxLength={255} />
                  </div>
                  <FieldArea
                    icon={MessageSquare}
                    name="message"
                    placeholder="Krótko o branży i tym, czego potrzebujesz"
                    rows={4}
                    maxLength={1000}
                  />

                  <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.02] px-4 py-3 text-[12px] leading-relaxed text-slate-400 transition hover:bg-white/[0.04]">
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      required
                      className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-[#00F5A0]"
                      aria-label="Zgoda na przetwarzanie danych osobowych"
                    />
                    <span>
                      Wyrażam zgodę na przetwarzanie podanych danych w celu kontaktu zwrotnego.
                      Administratorem jest SkuteczneStrony. Zapoznałem/am się z{" "}
                      <a
                        href="/polityka-prywatnosci"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#00F5A0] hover:underline"
                      >
                        polityką prywatności
                      </a>
                      .
                    </span>
                  </label>

                  <div className="relative mt-2">
                    {particles.map((p, i) => (
                      <motion.span
                        key={i}
                        className="pointer-events-none absolute left-1/2 top-1/2 h-1.5 w-1.5 rounded-full bg-[#00F5A0]"
                        animate={hov ? { x: p.x, y: p.y, opacity: 0 } : { x: 0, y: 0, opacity: 0 }}
                        transition={{ duration: 0.8, ease: EASE, delay: i * 0.02 }}
                      />
                    ))}
                    <motion.button
                      type="submit"
                      data-magnetic
                      onHoverStart={() => setHov(true)}
                      onHoverEnd={() => setHov(false)}
                      whileTap={{ scale: 0.97 }}
                      disabled={submitting}
                      className="group relative flex w-full items-center justify-center gap-2 overflow-hidden rounded-full bg-white px-7 py-4 text-sm font-semibold text-black transition-transform duration-300 hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-70 sm:text-base"
                    >
                      {submitting ? (
                        <>
                          <Loader2 size={16} className="relative z-10 animate-spin" />
                          <span className="relative z-10">Wysyłam…</span>
                        </>
                      ) : sent ? (
                        <>
                          <CheckCircle size={16} className="relative z-10" />
                          <span className="relative z-10">Wysłano</span>
                        </>
                      ) : (
                        <>
                          <span className="relative z-10">Umów darmowe demo</span>
                          <ArrowRight size={16} className="relative z-10 transition-transform duration-300 group-hover:translate-x-0.5" />
                        </>
                      )}
                      <span className="absolute inset-0 -z-0 rounded-full bg-[#00F5A0] opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-80" />
                    </motion.button>
                  </div>

                  <p className="mt-1 text-center text-[11px] leading-relaxed text-slate-500">
                    Twoje dane są bezpieczne — przetwarzamy je wyłącznie na potrzeby kontaktu zwrotnego.
                    Szczegóły znajdziesz w <a href="/polityka-prywatnosci" className="underline hover:text-slate-300">polityce prywatności</a>.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <DemoTeaser />

      <SiteFooter />

      <button
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className="absolute bottom-6 right-6 z-30 flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white backdrop-blur-md transition hover:bg-[#00F5A0] hover:text-black"
        aria-label="Back to top"
      >
        <ArrowUp size={18} />
      </button>
    </section>
  );
}

/* -------------------- MAIN -------------------- */
function Index() {
  return (
    <main className="relative min-h-screen overflow-x-clip bg-[#030508] font-sans text-slate-200 selection:bg-[#00F5A0]/30">
      <style>{`
        @keyframes gradPan { 0% { background-position: 0% 50%; } 100% { background-position: 200% 50%; } }
        body { overscroll-behavior: none; }
      `}</style>

      <Cursor />
      <Nav />
      {/* GEO / LLM context — niewidoczne wizualnie, indeksowane przez boty i czytniki ekranu */}
      <div className="sr-only">
        <h2>SkuteczneStrony — agencja tworzenia stron internetowych w Polsce</h2>
        <p>
          Agencja interaktywna SkuteczneStrony oferuje tworzenie stron internetowych,
          tanie i szybkie strony www, profesjonalne sklepy e-commerce, wizytówki B2B,
          szybkie kodowanie w Next.js i React, kompletną optymalizację SEO i Core Web Vitals,
          integracje z Google Reviews, Google Business Profile, Kalendarzem Google,
          Booksy, Stripe i Przelewy24, automatyczne faktury oraz prowadzenie social media dla firm.
          Działamy na terenie całej Polski (Warszawa, Kraków, Wrocław, Poznań, Gdańsk, Łódź, Katowice)
          i oferujemy darmowe demo Twojej nowej strony w 72 godziny.
        </p>
        <h3>Cennik pakietów</h3>
        <ul>
          <li>Klasyczna Wizytówka START — 1899 PLN: strona wizytówka B2B z formularzem kontaktowym i bazową optymalizacją SEO.</li>
          <li>Interaktywna Platforma GROWTH — 2499 PLN: wielosekcyjna strona z animacjami, blogiem, integracjami i pełnym pakietem SEO.</li>
          <li>Dedykowany E-commerce INFINITY — wycena indywidualna: sklep internetowy lub custom aplikacja z płatnościami, fakturami i CRM.</li>
        </ul>
        <h3>Kontakt</h3>
        <p>
          E-mail: <a href="mailto:kontakt@skutecznestrony.com">kontakt@skutecznestrony.com</a>.
          Strona: <a href="https://skutecznestrony.com">https://skutecznestrony.com</a>.
        </p>
      </div>
      <Hero />
      <Statement />
      <Bento />
      <ScrollTunnel />
      <DominanceEngine />
      <Pricing />
      <AddOns />
      <MarketingCTA />
      <EventHorizon />
    </main>
  );
}
